import { ref, shallowRef } from 'vue'

import { ApiError } from '@/api/errors'
import { useToastStore } from '@/stores/toast'
import { t } from '@/i18n'

export interface RequestOptions {
  /** Не показывать тост при ошибке — например, у клиента может не быть промокодов */
  silent?: boolean
}

/**
 * Обёртка запроса в реактивное состояние. Повторяет паттерн `useApi` из
 * прошлого проекта: запрос не стартует сам, момент выполнения решает
 * вызывающий код, ошибка одновременно уходит в тост и остаётся в `error`.
 */
export function useRequest<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  options: RequestOptions = {},
) {
  const $toast = useToastStore()

  const data = shallowRef<TResult | null>(null)
  const error = ref<string | null>(null)
  const isLoading = ref(false)

  async function execute(...args: TArgs): Promise<TResult | null> {
    isLoading.value = true
    error.value = null
    try {
      data.value = await fn(...args)
      return data.value
    } catch (cause) {
      error.value = cause instanceof ApiError ? cause.message : t('errors.unknown')
      if (!options.silent) $toast.error(error.value)
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { data, error, isLoading, execute }
}
